/**
 * reco-warm.js - every page except /pages/preporuceno: while the visitor browses, precompute what the
 * recommendations page would show and keep it in localStorage (reco-page.js warm() -> snapshot
 * mtgkarte_reco_snap_v1), so that page paints its rows the moment its HTML is parsed.
 *
 * Deliberately tiny. The scripts (reco-track/graph/page.js) and data files (deck index, graph core +
 * shards, co-purchase) are only fetched when the visitor already has a browsing profile, the
 * connection is not in data-saver / 2G mode, and the browser is idle after load. warm() itself skips
 * the data when the saved snapshot matches the cart and is younger than 10 min (30 min with the same
 * profile). The data files are HTTP-cached, so repeated warm-ups mostly re-read the cache; parsing
 * and scoring run in a Web Worker so the page stays responsive (on the page only if no worker).
 *
 * Script tag (layout/theme.liquid) carries the URLs as data-* attributes:
 *   data-scripts        space-separated asset URLs, loaded in order
 *   data-deck-index / data-reco-graph / data-reco-graph-shard / data-copurchase   file URLs
 * Never throws into the page.
 */
(function () {
  'use strict';
  var W = window;
  var THROTTLE_MS = 20000;
  var WARM_KEY = 'mtgkarte_reco_warm_t';
  try {
    var me = document.currentScript;
    if (!me || W.__mtgRecoWarm) return;
    W.__mtgRecoWarm = true;
    var d = me.dataset;
    // the page computes (and saves) its own rows; /en/pages/... and similar locale prefixes included
    if (/\/pages\/preporuceno(?:[/?#]|$)/i.test(location.pathname)) return;
    var c = navigator.connection;
    if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) return;

    var cfg = { deckIndex: d.deckIndex, recoGraph: d.recoGraph, recoGraphShard: d.recoGraphShard, copurchase: d.copurchase };
    var scripts = String(d.scripts || '').split(/\s+/).filter(Boolean);

    var hasProfile = function () {
      try {
        var p = JSON.parse(localStorage.getItem('mtgkarte_reco_v1') || 'null');
        // the page needs 3 entries including the cart; warm() re-checks with the cart
        return !!p && Object.keys(p.viewed || {}).length + (p.removed || []).length >= 2;
      } catch (e) { return false; }
    };
    // Read-only here: the stamp is written only when warm() actually starts computing (cfg.onStart),
    // so a page where warm() had nothing to do (profile still too small) does not block the next one.
    var throttled = function () {
      try {
        var last = Number(localStorage.getItem(WARM_KEY)) || 0;
        return Date.now() - last < THROTTLE_MS;
      } catch (e) { return true; }
    };
    cfg.onStart = function () { try { localStorage.setItem(WARM_KEY, String(Date.now())); } catch (e) {} };

    // The heavy part (parsing ~4 MB of JSON, scoring) runs in a Web Worker built from a blob that
    // imports reco-graph.js + reco-page.js (their pure half, no DOM). The worker page cannot run
    // at all (CSP, old browser) -> reject, and warm() computes on the page as before.
    var abs = function (u) { try { return new URL(u, location.href).href; } catch (e) { return u; } };
    var workerScripts = scripts.filter(function (s) { return /reco-(graph|page)\.js/.test(s); }).map(abs);
    var WORKER_TIMEOUT = 30000;
    cfg.worker = function (job) {
      return new Promise(function (resolve, reject) {
        if (!W.Worker || !W.Blob || !W.URL || workerScripts.length !== 2) return reject(new Error('no worker'));
        var w = null, url = null, done = false, timer = null;
        var finish = function (fn, v) {
          if (done) return; done = true; clearTimeout(timer);
          try { if (w) w.terminate(); } catch (e) {}
          try { if (url) URL.revokeObjectURL(url); } catch (e) {}
          fn(v);
        };
        try {
          var src = 'importScripts(' + workerScripts.map(function (s) { return JSON.stringify(s); }).join(',') + ');\n' +
            'onmessage=function(e){self.MtgRecoPage.warmRows(e.data).then(function(r){postMessage({ok:true,r:r});},' +
            'function(err){postMessage({ok:false,e:String(err)});});};';
          url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
          w = new Worker(url);
        } catch (e) { return finish(reject, e); }
        // a slow phone may take long - give up quietly (no snapshot this time) rather than retry on the page
        timer = setTimeout(function () { finish(resolve, null); }, WORKER_TIMEOUT);
        w.onmessage = function (e) {
          var d = e.data || {};
          if (d.ok) finish(resolve, d.r); else finish(reject, new Error(d.e || 'worker failed'));
        };
        w.onerror = function (e) { try { e.preventDefault(); } catch (x) {} finish(reject, new Error('worker error')); };
        w.postMessage(job);
      });
    };
    var loadScript = function (src) {
      return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src; s.async = false;
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });
    };
    var ready = null;
    var loadAll = function () {
      if (!ready) {
        ready = scripts.reduce(function (p, src) { return p.then(function () { return loadScript(src); }); }, Promise.resolve())
          .then(function () { return !!(W.MtgRecoPage && W.MtgRecoPage.warm && W.MtgRecoGraph); });
        ready.catch(function () { ready = null; });
      }
      return ready;
    };
    var run = function (force) {
      try {
        if (!hasProfile() || (force !== true && throttled())) return;
        loadAll().then(function (ok) { if (ok) return W.MtgRecoPage.warm(cfg); }).catch(function () {});
      } catch (e) {}
    };
    var idle = function (fn) {
      if (W.requestIdleCallback) W.requestIdleCallback(fn, { timeout: 5000 }); else setTimeout(fn, 1000);
    };
    var schedule = function () { setTimeout(function () { idle(function () { run(false); }); }, 1500); };

    if (document.readyState === 'complete') schedule(); else W.addEventListener('load', schedule);
    // cart changed (reco-tracker.js event): the saved rows may now include a card that is in the cart
    var t = null;
    document.addEventListener('mtgreco:cart-changed', function () {
      clearTimeout(t);
      t = setTimeout(function () { idle(function () { run(true); }); }, 3000);
    });
  } catch (e) {}
})();
